<template>
  <PModal
    title="Versions du document"
    :model-value="modelValue"
    @update:model-value="emit('update:modelValue', $event)"
    @close="onClose"
    size="lg"
  >
    <template #activator="{}">
      <span class="hidden"></span>
    </template>

    <template #default="{ titleId }">
      <PTable
        :table="table"
        :caption="document.name + document.extension"
        :aria-labelledby="titleId"
        class="max-lg:[&_.p-table-cell]:p-1 [&>caption]:text-ellipsis [&>caption]:overflow-y-hidden"
        :version="2"
      >
        <template #column.creationDate="{ row }">
          <span class="lg:whitespace-nowrap text-sm">
            {{ dayjs(row.creationDate).format("DD/MM/YYYY - HH[h]mm") }}
          </span>
        </template>
        <template #column.version="{ row }">
          <PButton
            :to="linksMap[getKey(row)]"
            v-if="linksMap[getKey(row)]"
            mode="primary"
            size="sm"
            data-sentry-id="DocVersionsDlBtn-{{ row.id }}"
          >
            <OhVueIcon :name="dlIcon" class="mr-1" />
            {{ row.version }}
          </PButton>
          <PButton
            v-else
            @mouseenter="fetchLink(row)"
            mode="primary"
            size="sm"
            data-sentry-id="DocVersionsFetchLinkBtn-{{ row.id }}"
          >
            <OhVueIcon :name="dlIcon" class="mr-1" />
            {{ row.version }}
          </PButton>
        </template>
      </PTable>
    </template>
  </PModal>
</template>

<script setup lang="ts">
import { PModal, PTable, type PastaTableColumnDefArgs, usePastaTable, PButton, useIcons } from "@pasta/box-v2";
import { type LetterFileDocumentDto } from "../dto/letter-file-document.dto";
import { computed, ref, watch } from "vue";
import { useLetterFileStore } from "../stores/letter-file.store";
import dayjs from "dayjs";
import { RiDownload2Fill } from "oh-vue-icons/icons";
import { OhVueIcon } from "oh-vue-icons";
import { useDocumentVersionStore } from "../stores/document-version.store";
import { DocumentVersionModifiedSource } from "../dto/document-version.dto";
import { useSimpleFeatureStore } from "../stores/simple-features.store";

interface Props {
  modelValue: boolean;
  document: LetterFileDocumentDto;
  disabled?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{ (e: "update:modelValue", value: boolean): void }>();

const [dlIcon] = useIcons(RiDownload2Fill);

const simpleFeatureStore = useSimpleFeatureStore();
const letterFileStore = useLetterFileStore();
const letterFile = computed(() => letterFileStore.getCurrent()!);
const docVersionStore = useDocumentVersionStore();

watch(
  () => props.modelValue,
  async () => {
    if (props.modelValue) {
      await docVersionStore.fetchForDoc(props.document.id, letterFile.value.id);
    } else {
      linksMap.value = {};
    }
  }
);

const columns: PastaTableColumnDefArgs<{
  author: string;
  creationDate: string;
  version: string;
}>[] = [
  { type: "simple", key: "creationDate", label: "Date", weight: 1 },
  {
    type: "simple",
    key: "author",
    label: "Modifié par",
    cssClass: "text-sm",
    weight: 2,
  },
  { type: "simple", key: "version", label: "Version", weight: 2 },
];
const data = computed<{ author: string; creationDate: string; version: string }[]>(() => {
  return docVersionStore.list.map((v, index) => {
    return {
      ...v,
      version: `Version ${
        index === 0 ? "actuelle" : docVersionStore.list.length - index
      } ${getLabelFromModifiedSource(v.modifiedSource)}`,
    };
  });
});
const table = usePastaTable({ columns, data });

const linksMap = ref<Record<string, string>>({});

function getKey(doc: { id: number }) {
  return `${doc.id}`;
}

async function fetchLink(doc: { id: number; author: string; creationDate: string; version: string }) {
  const key = getKey(doc);
  const found = linksMap.value[key];
  if (found) {
    return found;
  }

  let link: string;
  {
    link = await docVersionStore.getLink(doc.id, props.document.id, letterFile.value.id);
  }

  if (simpleFeatureStore.tmpDisableLinksRefacto || simpleFeatureStore.linkShortenerEnabled) {
    linksMap.value[key] = link;
  } else {
    linksMap.value[key] = `${link}&asAttachment=true`;
  }
}

function getLabelFromModifiedSource(modifiedSource: DocumentVersionModifiedSource) {
  switch (modifiedSource) {
    case DocumentVersionModifiedSource.SIGN:
      return "signée";
    case DocumentVersionModifiedSource.PADES:
      return "signée + identitée renforcée";
    case DocumentVersionModifiedSource.WOPI:
      return "Collabora";
    case DocumentVersionModifiedSource.REPLACE:
      return "ajoutée";
    case DocumentVersionModifiedSource.K2000:
      return "modifiée sur poste";
    case DocumentVersionModifiedSource.ORIGINAL_UPLOAD:
    default:
      return "";
  }
}

function onClose() {
  docVersionStore.list = [];
  emit("update:modelValue", false);
}
</script>
